import { FC, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';

import { useResetPasswordFormManagement } from '../hooks';
import { PASSWORD_MAX_LENGTH } from '../utils/passwordPolicy';
import {
  FieldsValidationErrorAlert,
  FormTextField,
  PasswordField,
  PasswordMatchNotice,
  PasswordRulesChecklist,
} from '../components';
import { MessageModal } from '../modals';

const keyPrefix = 'forms.ResetPasswordForm';

export const ResetPasswordForm: FC = (): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix });
  const [showErroMessage, setShowErroMessage] = useState<boolean>(false);
  const {
    clearErrorMessages,
    fieldsValidationErr,
    genericErr,
    loading,
    methods,
    cancel,
    isPasswordChangePending,
    submit,
  } = useResetPasswordFormManagement();

  const newPassword: string = methods.watch('newPassword') ?? '';
  const confirmPassword: string = methods.watch('confirmPassword') ?? '';

  useEffect(() => {
    !!genericErr && setShowErroMessage(true);
  }, [genericErr]);

  return (
    <>
      {showErroMessage && (
        <MessageModal
          message={genericErr!}
          okLabel={t('modals.genericErr.ok')}
          onClickOk={() => {
            setShowErroMessage(false);
            clearErrorMessages();
          }}
          onClose={() => {
            setShowErroMessage(false);
            clearErrorMessages();
          }}
        />
      )}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <Stack spacing={2} pt={2}>
            {fieldsValidationErr && (
              <FieldsValidationErrorAlert error={fieldsValidationErr} />
            )}
            <FormTextField
              name='actualPassword'
              label={t('actualPassword.label')}
              disabled={true}
              inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
            />
            <PasswordField
              name='newPassword'
              label={t('newPassword.label')}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            <PasswordRulesChecklist
              value={newPassword}
              showErrors={methods.formState.isSubmitted}
            />
            <PasswordField
              name='confirmPassword'
              label={t('confirmPassword.label')}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            <PasswordMatchNotice
              confirmation={confirmPassword}
              password={newPassword}
            />
            <Stack direction='row' pt={2} spacing={2}>
              {isPasswordChangePending && (
                <Button
                  type='button'
                  color='secondary'
                  variant='outlined'
                  onClick={cancel}
                  fullWidth
                >
                  {t('cancel.label')}
                </Button>
              )}
              <Button
                type='submit'
                variant='contained'
                disabled={loading || methods.formState.isSubmitting}
                fullWidth
              >
                {t('submit.label')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};
